// Field Radar · TenantBridge Mario Dashboard.
//
// Sit-between-tenant-and-WO-system control panel. Mario logs an incoming
// tenant report → mints a magic-link → chats with tenant → routes to
// MaintainX (email fwd) or Field Radar WO (internal) or closes.
//
// Chew-first (LaSalle pilot) — landlord picker will land in Sprint 5 when
// this generalizes.
import { useEffect, useMemo, useState } from "react";
import {
  Plus, PaperPlaneTilt, Copy, LinkSimple, Fire, CheckCircle,
  Clock, ArrowsCounterClockwise, Envelope, Wrench, XCircle, User, Calendar,
  DownloadSimple, ShieldCheck, Image as ImageIcon, Trash,
} from "@phosphor-icons/react";
import { AdminShell as Layout } from "@/components/Layout";
import EntryNoticeModal from "@/components/EntryNoticeModal";
import DispatchModal from "@/components/DispatchModal";
import { toast } from "sonner";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";

const PRIORITY_TONES = {
  emergency: "bg-red-100 text-red-800 border-red-500",
  urgent:    "bg-amber-100 text-amber-800 border-amber-500",
  normal:    "bg-slate-100 text-slate-700 border-slate-300",
  low:       "bg-slate-50 text-slate-500 border-slate-200",
};

const STATUS_TONES = {
  open:             "bg-cyan-100 text-cyan-800 border-cyan-500",
  awaiting_tenant:  "bg-indigo-100 text-indigo-800 border-indigo-500",
  awaiting_staff:   "bg-amber-100 text-amber-800 border-amber-500",
  closed:           "bg-slate-100 text-slate-500 border-slate-300",
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return "";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function NewThreadModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    address: "5346 Chew Ave", unit: "",
    subject: "", description: "", priority: "normal",
    tenant_name: "", tenant_email: "", tenant_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const canSave = form.address.trim() && form.subject.trim();
  const submit = async () => {
    if (!canSave) { toast.error("Address + subject required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/tenant-bridge/threads", form);
      toast.success("Thread created");
      onCreated(r.data.thread);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't create")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="tb-new-modal">
      <div className="w-full max-w-md bg-white border-4 border-[#0f172a] shadow-[8px_8px_0px_#0f172a]"
        onClick={e => e.stopPropagation()}>
        <div className="p-3 border-b-2 border-slate-200 flex items-center justify-between">
          <div className="font-black uppercase tracking-widest text-sm">Log a tenant report</div>
          <button onClick={onClose} className="text-slate-400 hover:text-black"><XCircle size={20}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Property address</label>
              <input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                data-testid="tb-new-address" className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm mt-1"/>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Unit</label>
              <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value.toUpperCase()})}
                data-testid="tb-new-unit" placeholder="3B"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm mt-1 font-mono uppercase"/>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Subject (what did the tenant report?)</label>
            <input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
              data-testid="tb-new-subject" placeholder="Toilet leaking around base"
              className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm mt-1"/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Details</label>
            <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              data-testid="tb-new-desc" placeholder="Called around 9am, wants to be home for the visit"
              className="w-full px-3 py-2 border-2 border-[#0f172a] text-sm mt-1 resize-none"/>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Priority</label>
            <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}
              data-testid="tb-new-priority"
              className="w-full h-11 px-2 border-2 border-slate-300 text-sm mt-1 bg-white">
              <option value="low">Low — cosmetic</option>
              <option value="normal">Normal — schedule this week</option>
              <option value="urgent">Urgent — schedule today</option>
              <option value="emergency">Emergency — dispatch now</option>
            </select>
          </div>
          <div className="pt-3 border-t-2 border-slate-100">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Tenant contact (used for magic-link)</div>
            <div className="grid grid-cols-2 gap-2">
              <input value={form.tenant_name} onChange={e => setForm({...form, tenant_name: e.target.value})}
                data-testid="tb-new-tenant-name" placeholder="Name"
                className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              <input value={form.tenant_phone} onChange={e => setForm({...form, tenant_phone: e.target.value})}
                data-testid="tb-new-tenant-phone" placeholder="Phone"
                className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <input value={form.tenant_email} onChange={e => setForm({...form, tenant_email: e.target.value})}
              data-testid="tb-new-tenant-email" placeholder="Email (optional)"
              className="mt-2 w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
        </div>
        <div className="p-3 border-t-2 border-slate-200 bg-slate-50 flex gap-2 justify-end">
          <button onClick={onClose} className="h-10 px-4 border-2 border-slate-300 font-black uppercase text-xs tracking-widest hover:bg-white">Cancel</button>
          <button onClick={submit} disabled={busy || !canSave} data-testid="tb-new-submit"
            className="h-10 px-4 bg-[#0f172a] hover:bg-slate-800 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
            {busy ? "Creating…" : "Create + mint link"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-300",
    cyan: "bg-cyan-100 text-cyan-800 border-cyan-500",
    amber: "bg-amber-100 text-amber-800 border-amber-500",
    red: "bg-red-100 text-red-800 border-red-500",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-500",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-500",
  };
  return (
    <div className={`px-3 py-2 border-2 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="text-[10px] font-black uppercase tracking-widest">{label}</div>
      <div className="font-black text-2xl mt-0.5">{value}</div>
    </div>
  );
}

function ThreadDetail({ thread, onClose, onReload, onRouted }) {
  const [msgText, setMsgText] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showRoute, setShowRoute] = useState(false);

  const load = () => api.get(`/tenant-bridge/threads/${thread.id}`)
    .then(r => setDetail(r.data)).catch(() => toast.error("Couldn't load"));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [thread.id]);

  const magicUrl = `${window.location.origin}/t/${thread.magic_token}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(magicUrl); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const smsHref = () => {
    const encodedMsg = encodeURIComponent(
      `${thread.address || "Property"} — you have a maintenance update. Tap to view: ${magicUrl}`
    );
    return `sms:?&body=${encodedMsg}`;
  };
  const emailHref = () => {
    const subj = encodeURIComponent(`Maintenance update · ${thread.address || ""} ${thread.unit || ""}`);
    const body = encodeURIComponent(
      `Hi — we've logged your maintenance report. You can view it and reply here:\n\n${magicUrl}\n\nThanks,\n${thread.address || "Property Management"}`
    );
    return `mailto:?subject=${subj}&body=${body}`;
  };

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tenant-bridge/threads/${thread.id}/messages`, { text: msgText });
      setMsgText("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't send")); }
    finally { setBusy(false); }
  };

  const route = async (target, ref = "") => {
    if (!window.confirm(`Route this thread to ${target === "maintainx" ? "MaintainX" : target === "field_radar_wo" ? "Field Radar WO queue" : "Closed"}?`)) return;
    setBusy(true);
    try {
      await api.post(`/tenant-bridge/threads/${thread.id}/route`, { target, ref });
      toast.success("Routed — thread closed");
      onRouted();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't route")); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm(
      "Cancel this tenant request?\n\n" +
      "• The tenant's magic link will stop working immediately\n" +
      "• Thread will be hidden from the inbox (recoverable via cancelled filter)\n" +
      "• Nothing is deleted"
    )) return;
    setBusy(true);
    try {
      await api.post(`/tenant-bridge/threads/${thread.id}/cancel`);
      toast.success("Request cancelled — tenant link is now invalid");
      onRouted();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't cancel")); }
    finally { setBusy(false); }
  };

  const messages = detail?.messages || [];
  const t = detail?.thread || thread;
  const closed = t.status === "closed";

  return (
    <div className="bg-white border-2 border-[#0f172a] flex flex-col h-[calc(100vh-220px)]" data-testid="tb-detail">
      <div className="p-3 border-b-2 border-slate-200 bg-slate-50 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-black text-base truncate" data-testid="tb-detail-subject">{t.subject}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {t.address} {t.unit && `· ${t.unit}`} · {t.tenant_display}
          </div>
        </div>
        {!closed && t.status !== "cancelled" && (
          <button onClick={cancel} disabled={busy}
            data-testid="tb-detail-cancel"
            title="Cancel request — invalidates the tenant's link"
            className="shrink-0 h-8 px-2 border-2 border-rose-500 text-rose-700 hover:bg-rose-50 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            <Trash weight="bold" size={12}/> Cancel
          </button>
        )}
        <button onClick={onClose} className="text-slate-400 hover:text-black shrink-0">
          <XCircle size={20}/>
        </button>
      </div>

      {/* Magic link row */}
      <div className="p-2 bg-cyan-50 border-b-2 border-cyan-200 flex items-center gap-2 text-[11px]">
        <LinkSimple size={14} className="text-cyan-600 shrink-0"/>
        <div className="font-mono text-cyan-800 truncate flex-1" data-testid="tb-magic-link">{magicUrl}</div>
        <button onClick={copyLink} data-testid="tb-copy-link"
          className="p-1 hover:bg-cyan-100 border border-cyan-300" title="Copy link">
          <Copy size={12}/>
        </button>
        <a href={smsHref()} data-testid="tb-send-sms"
          className="px-2 py-1 bg-white border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest hover:bg-slate-100">
          Text
        </a>
        <a href={emailHref()} data-testid="tb-send-email"
          className="px-2 py-1 bg-white border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest hover:bg-slate-100">
          Email
        </a>
      </div>

      {t.description && (
        <div className="p-3 border-b border-slate-100 text-xs text-slate-700 italic">
          Initial report: {t.description}
        </div>
      )}

      {/* Entry notices strip — one per pending/signed notice on this thread */}
      {(detail?.entry_notices || []).length > 0 && (
        <div className="border-b-2 border-amber-200 bg-amber-50 p-2 space-y-2" data-testid="tb-entry-notices">
          {(detail.entry_notices || []).map(n => {
            const cw = n.chosen_window || {};
            const tone = n.status === "signed" ? "emerald" : n.status === "declined" ? "red" : "amber";
            const toneClass = {
              emerald: "border-emerald-500 bg-emerald-50 text-emerald-800",
              red: "border-red-500 bg-red-50 text-red-800",
              amber: "border-amber-500 bg-white text-amber-800",
            }[tone];
            return (
              <div key={n.id} className={`p-2 border-2 ${toneClass}`} data-testid={`tb-notice-${n.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest">
                    {n.status === "signed" ? <ShieldCheck weight="fill" size={14}/> :
                     n.status === "declined" ? <XCircle weight="fill" size={14}/> :
                     <Calendar weight="fill" size={14}/>}
                    Entry · {n.status}
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tenant-bridge/threads/${thread.id}/entry-notices/${n.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("access_token") || "")}`}
                    target="_blank" rel="noreferrer" data-testid={`tb-notice-pdf-${n.id}`}
                    className="text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1 hover:underline">
                    <DownloadSimple size={11}/> PDF
                  </a>
                </div>
                <div className="mt-1 text-xs">{n.reason}</div>
                {n.status === "signed" && cw.start && (
                  <div className="mt-1 text-[11px] font-mono">
                    ✓ {cw.start} → {cw.end} · signed by {n.signed_name}
                  </div>
                )}
                {n.status === "pending" && (
                  <div className="mt-1 text-[10px] italic">
                    Waiting on tenant · {n.windows.length} window{n.windows.length === 1 ? "" : "s"} offered
                  </div>
                )}
                {n.status === "declined" && n.declined_reason && (
                  <div className="mt-1 text-[11px] italic">Declined: "{n.declined_reason}"</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50" data-testid="tb-message-list">
        {messages.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-xs font-black uppercase tracking-widest">
            No messages yet — text the tenant the link above to start the thread.
          </div>
        )}
        {messages.map((m, i) => {
          const photos = m.photos || [];
          return (
            <div key={i} className={`flex ${m.from === "staff" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] p-2.5 text-sm border-2 ${
                m.from === "staff" ? "bg-cyan-100 border-cyan-500" : "bg-white border-[#0f172a]"
              }`}>
                <div className="text-[9px] font-black uppercase tracking-widest opacity-60 mb-0.5">
                  {m.from === "staff" ? m.author || "Staff" : "Tenant"} · {new Date(m.at).toLocaleString()}
                </div>
                {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
                {photos.length > 0 && (
                  <div className="mt-1.5 grid grid-cols-3 gap-1" data-testid={`tb-admin-msg-photos-${i}`}>
                    {photos.map((p, pi) => (
                      <a key={pi} href={fileUrl(p.path)} target="_blank" rel="noreferrer"
                        data-testid={`tb-admin-msg-photo-${i}-${pi}`}
                        className="block relative border-2 border-slate-300 hover:border-cyan-500 aspect-square overflow-hidden bg-slate-100"
                        title="Click to view full size">
                        <img src={fileUrl(p.path)} alt={`photo ${pi + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                      </a>
                    ))}
                  </div>
                )}
                {photos.length > 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest opacity-60">
                    <ImageIcon weight="fill" size={10}/> {photos.length} photo{photos.length > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer or routed banner */}
      {closed ? (
        <div className="p-3 border-t-2 border-slate-200 bg-slate-100 text-xs">
          <div className="font-black uppercase tracking-widest text-slate-500">Closed</div>
          <div className="mt-1 text-slate-700">
            Routed to <b>{t.routed_to}</b>
            {t.routed_ref && <> · ref: <span className="font-mono">{t.routed_ref}</span></>}
          </div>
        </div>
      ) : (
        <>
          <div className="p-2 border-t border-slate-200 flex gap-2">
            <input value={msgText} onChange={e => setMsgText(e.target.value)}
              onKeyDown={e => e.key === "Enter" && sendMsg()}
              data-testid="tb-msg-input"
              placeholder="Reply to tenant…"
              className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            <button onClick={sendMsg} disabled={busy || !msgText.trim()} data-testid="tb-msg-send"
              className="h-11 px-4 bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-40">
              <PaperPlaneTilt weight="fill" size={16}/>
            </button>
          </div>

          {/* Routing bar */}
          <div className="p-2 border-t-2 border-slate-200 bg-slate-50 flex flex-wrap gap-2 justify-end">
            <button onClick={() => setShowEntryModal(true)} data-testid="tb-request-entry-btn"
              className="h-10 px-3 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-1 mr-auto">
              <Calendar weight="fill" size={12}/> Request 24-hr Entry
            </button>
            <button onClick={() => setShowDispatchModal(true)} data-testid="tb-route-maintainx"
              className="h-10 px-3 bg-white border-2 border-[#0f172a] font-black uppercase text-[10px] tracking-widest hover:bg-slate-100 flex items-center gap-1">
              <Envelope size={12}/> Send to MaintainX
            </button>
            <button onClick={() => route("field_radar_wo")} data-testid="tb-route-fr"
              className="h-10 px-3 bg-cyan-500 hover:bg-cyan-600 text-white font-black uppercase text-[10px] tracking-widest flex items-center gap-1">
              <Wrench size={12}/> Send to FR WO
            </button>
            <button onClick={() => route("closed")} data-testid="tb-route-closed"
              className="h-10 px-3 bg-white border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest hover:bg-slate-100 flex items-center gap-1 text-slate-500">
              <CheckCircle size={12}/> Close (no work)
            </button>
          </div>
        </>
      )}

      {showEntryModal && (
        <EntryNoticeModal threadId={thread.id} threadSubject={t.subject}
          onClose={() => setShowEntryModal(false)}
          onCreated={() => { setShowEntryModal(false); load(); onReload && onReload(); }}/>
      )}
      {showDispatchModal && (
        <DispatchModal threadId={thread.id} unit={t.unit}
          onClose={() => setShowDispatchModal(false)}
          onDispatched={() => { setShowDispatchModal(false); onRouted(); }}/>
      )}
    </div>
  );
}

export default function AdminTenantBridge() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  async function load() {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const r = await api.get(`/tenant-bridge/threads${q}`);
      setThreads(r.data.threads || []);
    } catch { toast.error("Couldn't load threads"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const stats = useMemo(() => {
    const s = { total: threads.length, open: 0, awaiting_staff: 0, awaiting_tenant: 0, closed: 0 };
    threads.forEach(t => { s[t.status] = (s[t.status] || 0) + 1; });
    return s;
  }, [threads]);

  const selected = threads.find(t => t.id === selectedId);

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="admin-tenant-bridge">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Field Radar · TenantBridge</div>
            <h1 className="text-2xl font-black mt-1">TenantBridge Inbox</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every incoming tenant report. Chat via magic-link (no app install), request entry, route to MaintainX or Field Radar WOs. Anonymous contact firewall on properties that need it.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="tb-new-thread-btn"
            className="h-11 px-4 bg-cyan-500 hover:bg-cyan-600 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2">
            <Plus weight="bold" size={14}/> Log a report
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
          <StatChip label="Total" value={stats.total} tone="slate" testid="tb-stat-total"/>
          <StatChip label="Open" value={stats.open} tone="cyan" testid="tb-stat-open"/>
          <StatChip label="Waiting on you" value={stats.awaiting_staff || 0} tone="red" testid="tb-stat-waiting-staff"/>
          <StatChip label="Waiting on tenant" value={stats.awaiting_tenant || 0} tone="indigo" testid="tb-stat-waiting-tenant"/>
          <StatChip label="Closed" value={stats.closed} tone="emerald" testid="tb-stat-closed"/>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* THREAD LIST */}
          <div className={`bg-white border-2 border-[#0f172a] ${selectedId ? "lg:col-span-2" : "lg:col-span-5"}`}>
            <div className="p-2 border-b-2 border-slate-200 bg-slate-50 flex items-center gap-1 flex-wrap">
              {["", "open", "awaiting_staff", "awaiting_tenant", "closed"].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  data-testid={`tb-filter-${s || "all"}`}
                  className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest border-2 ${
                    statusFilter === s ? "bg-[#0f172a] text-white border-[#0f172a]" : "bg-white border-slate-300 hover:bg-slate-100"
                  }`}>
                  {s || "All"}
                </button>
              ))}
            </div>
            <div className="divide-y divide-slate-100 max-h-[calc(100vh-320px)] overflow-y-auto">
              {loading && <div className="p-6 text-center text-slate-400 text-[10px] font-black uppercase tracking-widest">Loading…</div>}
              {!loading && threads.length === 0 && (
                <div className="p-8 text-center text-slate-400">
                  <div className="text-xs font-black uppercase tracking-widest">No threads yet</div>
                  <div className="text-[11px] mt-2">Click "Log a report" to create your first one.</div>
                </div>
              )}
              {threads.map(t => (
                <button key={t.id} onClick={() => setSelectedId(t.id)}
                  data-testid={`tb-thread-${t.id}`}
                  className={`w-full text-left p-3 hover:bg-cyan-50 ${
                    selectedId === t.id ? "bg-cyan-50 border-l-4 border-cyan-500" : ""
                  }`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-sm truncate">{t.subject}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                        {t.address}{t.unit && ` · ${t.unit}`} · {t.tenant_display}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 shrink-0">{timeAgo(t.updated_at)}</div>
                  </div>
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest border-2 ${STATUS_TONES[t.status] || STATUS_TONES.open}`}>
                      {t.status.replace("_", " ")}
                    </span>
                    <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest border-2 ${PRIORITY_TONES[t.priority] || PRIORITY_TONES.normal}`}>
                      {t.priority === "emergency" && <Fire size={9} weight="fill" className="inline mr-0.5"/>}
                      {t.priority}
                    </span>
                    {t.messages_count > 0 && (
                      <span className="text-[10px] text-slate-500">{t.messages_count} msg</span>
                    )}
                    {t.unread_from_tenant > 0 && (
                      <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-500 text-white">
                        {t.unread_from_tenant} new
                      </span>
                    )}
                    {t.routed_to && (
                      <span className="text-[10px] text-slate-400">→ {t.routed_to}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* DETAIL PANEL */}
          {selected && (
            <div className="lg:col-span-3">
              <ThreadDetail thread={selected}
                onClose={() => setSelectedId(null)}
                onReload={load}
                onRouted={() => { setSelectedId(null); load(); }}/>
            </div>
          )}
        </div>

        {showNew && (
          <NewThreadModal onClose={() => setShowNew(false)}
            onCreated={(t) => { setShowNew(false); setSelectedId(t.id); load(); }}/>
        )}
      </div>
    </Layout>
  );
}
